import {
  alertError,
  alertInfo,
  Button,
  ButtonSize,
  ButtonStyle,
  Checkbox,
  colors,
  getPromotionError,
  IGiftVoucher,
  Loading,
} from '@one-tree/library'
import React, { ReactElement, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { FaRegQuestionCircle } from 'react-icons/fa'
import { useOrganisation } from '../../../context/OrganisationProvider'
import { editPromotion } from '../../../helpers/APIHelper'
import { RoutePath } from '../../../types/Routes'
import {
  convertPromoResponseToPromotion,
  convertPromotionToPromoResponse,
  getPromoDiscount,
  Promotion,
  Promotions,
  StatusText,
} from '../promotion/PromotionHelper'

const Styles = styled.section`
  /* Increase specificity to override section styles */
  && {
    display: flex;
    flex-direction: column;
    align-items: start;
    row-gap: 15px;
  }
`
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  border: 1px solid ${colors.gray};
  th,
  td {
    padding: 8px;
  }
  th {
    padding-top: 9px;
    border-bottom: 1px solid ${colors.gray};
    background-color: ${colors.lightGray};
  }
  td:last-child {
    text-align: right;
  }
`
const Question = styled(FaRegQuestionCircle)`
  cursor: pointer;
  width: 17px;
  height: 17px;
  margin-left: -3px;
`
const Info = styled.p`
  margin: 0;
`
const CheckboxCell = styled.span<{ hasError: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ hasError }): string => (hasError ? colors.red : colors.darkerGray)};
`

type Props = {
  promotions: Promotions
  giftVoucher: IGiftVoucher
  onPromotionToggle: () => Promise<void>
}

export default function GiftPromotionTable(props: Props): ReactElement {
  const { promotions: init, giftVoucher, onPromotionToggle } = props
  const { organisation } = useOrganisation()
  const history = useHistory()
  const [promotions, setPromotions] = useState<Promotions>(init)
  const [loadingId, setLoadingId] = useState<number | null>(null)

  // Promotions track which gift items they apply to
  // The single active (best) promotion is decided server-side
  const handleToggle = async (
    promo: Promotion,
    isOn: boolean,
  ): Promise<void> => {
    setLoadingId(promo.id)
    const currentGiftItems = promo.giftItems ?? []
    const newGiftItems = isOn
      ? [...currentGiftItems, giftVoucher.id]
      : currentGiftItems.filter((id) => id !== giftVoucher.id)

    const res = await editPromotion({
      id: promo.id,
      promotion: { giftItems: newGiftItems },
    })

    // Replace updated promotion with the server response
    if (res) {
      const converted = convertPromoResponseToPromotion(res)
      setPromotions((p) => p.map((each) => (each.id === res.id ? converted : each)))
      await onPromotionToggle()
    }
    setLoadingId(null)
  }

  return (
    <Styles>
      <h3>Promotions</h3>
      {promotions.length ? (
        <>
          <Info>
            If more than one promotion is selected at once, the best deal for
            the customer will automatically be{' '}
            <span style={{ color: colors.blue, fontWeight: 'bold' }}>
              applied
            </span>
            .
          </Info>
          {giftVoucher.promotion && getPromotionError(giftVoucher) ? (
            <Info style={{ color: colors.red }}>
              No promotion is applied: the best promotion is not compatible with
              this gift&apos;s settings.
            </Info>
          ) : null}
          <Table>
            <thead>
              <tr>
                <th>Enable</th>
                <th>Name</th>
                <th>Discount</th>
                <th>Code</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {promotions.map((promo) => {
                const convertedPromo = convertPromotionToPromoResponse(promo)
                const testVoucher = { ...giftVoucher, promotion: convertedPromo }
                const error = getPromotionError(testVoucher) || undefined
                const value = promo.giftItems?.includes(giftVoucher.id) ?? false

                return (
                  <tr key={promo.id}>
                    <td>
                      {loadingId === promo.id ? (
                        <Loading />
                      ) : (
                        <CheckboxCell hasError={value && !!error} title={error}>
                          <Checkbox
                            value={value}
                            onChange={(isOn): Promise<void> => handleToggle(promo, isOn)}
                            isError={value && !!error}
                            disabled={!value && !!error}
                          />
                          {error ? (
                            <Question
                              onClick={(): void => (value ? alertError(error) : alertInfo(error))}
                            />
                          ) : null}
                        </CheckboxCell>
                      )}
                    </td>
                    <td>{promo.name}</td>
                    <td>
                      {getPromoDiscount(promo, organisation?.currencyCode)}
                    </td>
                    <td>{promo.code}</td>
                    <td>
                      <StatusText
                        promo={promo}
                        appliedId={giftVoucher.promotion?.id}
                        error={error}
                        checked={value}
                        stale={loadingId !== null}
                      />
                    </td>
                    <td>
                      <Button
                        buttonStyle={ButtonStyle.Secondary}
                        buttonSize={ButtonSize.Mini}
                        onClick={(): void => history.push(
                            `${RoutePath.ManagePromotions}/${promo.id}`,
                          )}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </>
      ) : null}
      <Button
        buttonStyle={ButtonStyle.Secondary}
        onClick={(): void => history.push(RoutePath.CreatePromotion)}
      >
        Create new promotion
      </Button>
    </Styles>
  )
}
